"use client";

import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Header } from "@/app/components/Header";
import { RegionStatesSection } from "@/app/components/settings/RegionStatesSection";
import { TemplatesSection } from "@/app/components/settings/TemplatesSection";
import { DefaultsSection } from "@/app/components/settings/DefaultsSection";
import { PipelineSection } from "@/app/components/settings/PipelineSection";

type SettingsTab = "region" | "templates" | "defaults" | "pipeline";

const TABS: { id: SettingsTab; label: string }[] = [
  { id: "region", label: "Region states" },
  { id: "templates", label: "Templates" },
  { id: "defaults", label: "Defaults" },
  { id: "pipeline", label: "Pipeline" },
];

function isSettingsTab(value: string | null): value is SettingsTab {
  return value === "region" || value === "templates" || value === "defaults" || value === "pipeline";
}

export default function SettingsPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen flex-col">
          <Header />
          <main className="mx-auto w-full max-w-5xl flex-1 px-6 py-8 text-sm text-[var(--color-text-secondary)]">
            Loading…
          </main>
        </div>
      }
    >
      <SettingsPageInner />
    </Suspense>
  );
}

function SettingsPageInner() {
  // ?tab= lets other pages deep-link into a specific section (e.g. the
  // old /scenario-templates redirect, or a future "configure region"
  // link from the listings filter) -- falls back to Region states.
  const searchParams = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [tab, setTab] = useState<SettingsTab>(isSettingsTab(requestedTab) ? requestedTab : "region");

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto flex w-full max-w-5xl flex-1 gap-6 px-6 py-8">
        <nav className="w-44 shrink-0 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="block w-full rounded-md px-3 py-2 text-left text-sm"
              style={
                tab === t.id
                  ? {
                      backgroundColor: "var(--color-accent-muted)",
                      color: "var(--color-accent-strong)",
                      fontWeight: 500,
                      borderLeft: "2px solid var(--color-accent)",
                    }
                  : { color: "var(--color-text-secondary)" }
              }
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="min-w-0 flex-1">
          {tab === "region" && <RegionStatesSection />}
          {tab === "templates" && <TemplatesSection />}
          {tab === "defaults" && <DefaultsSection />}
          {tab === "pipeline" && <PipelineSection />}
        </div>
      </main>
    </div>
  );
}
